import { api } from "@/lib/api";

/**
 * Prepares filters in the correct format for the server
 *
 * Handles transformation between client-side filter formats and server-side expectations,
 * with special handling for:
 * 1. Nested date objects (start_date: {from: '2025-05-14', to: '2025-05-15'})
 * 2. Date filters with _filter suffix (start_date_filter_from)
 * 3. Type and source_record_type array filters
 * 4. Text search mapping to name_filter for consistent behavior
 */
export function prepareFiltersForServer(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  // Create a copy of the filters to avoid modifying the original
  const preparedFilters: Record<string, unknown> = {};

  // Process each filter to match the server's expected format
  for (const [key, value] of Object.entries(filters)) {
    // Handle nested date objects (e.g., start_date: {from: '2025-05-14', to: '2025-05-15'})
    if (
      (key === "start_date" || key === "end_date" || key.endsWith("_date")) &&
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value)
    ) {
      const dateObj = value as Record<string, unknown>;

      // Extract from/to values from the nested object
      if ("from" in dateObj && dateObj.from) {
        preparedFilters[`${key}_from`] = dateObj.from;
      }

      if ("to" in dateObj && dateObj.to) {
        preparedFilters[`${key}_to`] = dateObj.to;
      }
    }
    // Keep sortColumn and sortDirection
    else if (key === "sortColumn" || key === "sortDirection") {
      preparedFilters[key] = value;
    }
    // Handle date filters with _filter suffix (e.g. start_date_filter_from, end_date_filter_to)
    else if (
      (key.includes("_filter") &&
        (key.endsWith("_from") || key.endsWith("_to"))) ||
      key.endsWith("date_from") ||
      key.endsWith("date_to")
    ) {
      // Extract the key without _filter suffix for date filters
      // e.g. start_date_filter_from -> start_date_from
      const dateKey =
        key.replace("_filter", "").replace(/(_from|_to)$/, "") +
        (key.endsWith("_from") ? "_from" : "_to");

      preparedFilters[dateKey] = value;
    }
    // Handle regular date range filters
    else if (
      key.endsWith("_from") ||
      key.endsWith("_to") ||
      key.endsWith("_min") ||
      key.endsWith("_max")
    ) {
      // Pass through as-is
      preparedFilters[key] = value;
    }
    // Text search handling
    else if (key === "search") {
      preparedFilters.name_filter = value; // Map search to name_filter for consistent behavior
    }
    // Handle all other *_filter keys by removing the _filter suffix
    else if (key.endsWith("_filter")) {
      // Keep name_filter as is to match campaign table behavior
      if (key === "name_filter") {
        preparedFilters[key] = value;
      } else {
        const baseKey = key.replace(/_filter$/, "");
        preparedFilters[baseKey] = value;
      }
    }
    // Other filters pass through as-is
    else {
      preparedFilters[key] = value;
    }
  }

  return preparedFilters;
}

export async function fetchMatchingCampaignCount(
  filters: Record<string, unknown>,
  campaignId: string | null,
  excludeCurrentCampaign: boolean = true,
  mode: "dynamic" | "static" = "dynamic",
  selectedCampaignIds: string[] = [],
): Promise<number> {
  if (!campaignId) return 0;

  try {
    // Prepare the filters for the server
    const preparedFilters = prepareFiltersForServer(filters);

    // Create the request payload
    const payload: Record<string, unknown> = {
      // Always include the mode parameter to explicitly indicate the intended mode
      mode: mode,
    };

    // In static mode, include campaign_ids and don't exclude current campaign
    if (mode === "static" && selectedCampaignIds.length > 0) {
      payload.campaign_ids = selectedCampaignIds;
      payload.exclude_current_campaign = false;
    }
    // For dynamic mode, respect exclude flag
    else {
      payload.exclude_current_campaign = excludeCurrentCampaign;
    }

    // Always include benchmark_filters if they exist
    if (preparedFilters && Object.keys(preparedFilters).length > 0) {
      payload.benchmark_filters = preparedFilters;
    }

    const response = await api.post<{ campaigns: Array<{ id: string }> }>(
      `/datahub/campaigns/${campaignId}/benchmark-campaigns/`,
      payload,
    );

    return response.campaigns.length;
  } catch (error) {
    console.error("Error fetching benchmark campaign count:", error);
    return 0;
  }
}
